/**
 * Pestaña "Infraestructura": la cuota de tráfico del servidor rentado.
 *
 * Tres decisiones de lectura, todas deliberadas:
 *
 * 1. **Sin muestras se dice que no hay medición**, no se pintan ceros. Un cero
 *    aquí se lee como "no hemos gastado nada" y es indistinguible de "nadie ha
 *    medido"; ese equívoco tuvo la página de uso de IA en cero durante meses.
 * 2. **La gráfica muestra sólo la SALIDA**, que es lo único que se cobra. La
 *    entrada va como dato al lado: es ~17 veces mayor y, puesta en la misma
 *    gráfica, se lleva la escala y hace ver la salida como una raya.
 * 3. **El medidor es el mismo componente que el del tope de tokens.** Las
 *    cuotas de AROC se ven iguales a propósito: misma barra, mismos estados,
 *    misma marca de proyección.
 */
import {
  Bar,
  BarChart,
  CartesianGrid,
  ReferenceLine,
  ResponsiveContainer,
  Tooltip,
  XAxis,
  YAxis,
} from "recharts";

import { QuotaMeter } from "@/components/QuotaMeter";
import { useInfraTraffic } from "@/hooks/useInfra";
import { formatBytes, formatMoneda, formatShortDate } from "@/lib/usageHelpers";

// Una sola serie: no hay paleta categórica que repartir ni leyenda que poner.
// El título ya dice qué se está pintando.
const AZUL = "#3b82f6";

export function InfraTrafficPanel() {
  const { data, isLoading, error } = useInfraTraffic();

  if (isLoading) return <div className="text-sm text-muted-foreground">Cargando…</div>;
  if (error) {
    return (
      <div className="text-sm text-destructive">
        No se pudo obtener el tráfico: {(error as Error).message}
      </div>
    );
  }
  if (!data) return null;

  if (!data.disponible) {
    return (
      <div className="rounded-lg border bg-card p-4 space-y-2">
        <h2 className="text-sm font-medium">Tráfico del servidor</h2>
        <p className="text-sm text-muted-foreground">
          Todavía no hay ninguna medición. No significa que no se haya gastado tráfico:
          significa que el colector no ha podido leer la API del proveedor.
        </p>
        <p className="text-xs text-muted-foreground">
          Falta encenderlo (<code>INFRA_TRAFFIC_ENABLED</code>) y cargarle un testigo de
          sólo lectura (<code>HETZNER_API_TOKEN</code>) en el archivo de variables del
          servidor.
        </p>
      </div>
    );
  }

  const cuota = data.incluido_bytes ?? 0;
  const salida = data.salida_bytes ?? 0;
  // 20 TB al mes repartidos parejo. Es la línea que delata el día que algo se
  // rompió, aunque el total del mes todavía se vea tranquilo.
  const presupuestoDiario = cuota > 0 ? cuota / 30 : null;

  return (
    <div className="space-y-4">
      <div className="grid grid-cols-1 md:grid-cols-4 gap-4">
        <Dato etiqueta="Salida del mes" valor={formatBytes(salida)} nota="es lo que se cobra" />
        <Dato
          etiqueta="De la cuota"
          valor={
            data.porcentaje_cuota === null
              ? "—"
              : `${data.porcentaje_cuota < 0.01 ? "<0.01" : data.porcentaje_cuota.toFixed(2)} %`
          }
          nota={`de ${formatBytes(cuota)} incluidos`}
        />
        <Dato
          etiqueta="Excedente proyectado"
          valor={formatMoneda(data.costo_excedente_proyectado, data.moneda)}
          nota={
            data.costo_excedente_proyectado === null
              ? "sin tarifa del proveedor"
              : `a fin de mes · hoy ${formatMoneda(data.costo_excedente, data.moneda)}`
          }
        />
        <Dato
          etiqueta="Renta del servidor"
          valor={formatMoneda(data.renta_mensual, data.moneda)}
          nota={[data.tipo_servidor, data.ubicacion].filter(Boolean).join(" · ") || "al mes"}
        />
      </div>

      <QuotaMeter
        title="Tráfico de salida del mes"
        scope={`${data.servidor ?? "servidor"} · ${data.proveedor ?? "proveedor"} · sólo se cobra la salida; la entrada (${formatBytes(data.entrada_bytes)}) es gratis`}
        used={salida}
        limit={cuota}
        format={formatBytes}
        projected={data.proyeccion_salida_bytes}
        resetsLabel={null}
      />

      <div className="rounded-lg border bg-card p-4">
        <h3 className="text-sm font-semibold">Salida por día</h3>
        <p className="mb-3 mt-1 text-xs text-muted-foreground">
          Últimos 30 días. La línea es el reparto parejo de la cuota; sirve para ver el
          día exacto en que algo empezó a mandar de más.
        </p>
        {data.serie_diaria.length === 0 ? (
          <p className="py-12 text-center text-sm text-muted-foreground">
            Todavía no hay días completos que comparar.
          </p>
        ) : (
          <ResponsiveContainer width="100%" height={240}>
            <BarChart
              data={data.serie_diaria}
              margin={{ top: 4, right: 16, bottom: 4, left: 8 }}
            >
              <CartesianGrid strokeDasharray="3 3" opacity={0.3} />
              <XAxis dataKey="dia" fontSize={11} tickFormatter={(d) => formatShortDate(d)} />
              <YAxis fontSize={11} tickFormatter={(v) => formatBytes(Number(v))} width={72} />
              <Tooltip
                formatter={(v) => [formatBytes(Number(v)), "Salida"]}
                labelFormatter={(d) => formatShortDate(String(d))}
              />
              {presupuestoDiario !== null && (
                <ReferenceLine
                  y={presupuestoDiario}
                  stroke="#94a3b8"
                  strokeDasharray="4 4"
                  label={{ value: "reparto parejo", fontSize: 10, position: "insideTopRight" }}
                />
              )}
              <Bar dataKey="salida_bytes" fill={AZUL} radius={[4, 4, 0, 0]} />
            </BarChart>
          </ResponsiveContainer>
        )}
      </div>

      {data.medido_en && (
        <p className="text-xs text-muted-foreground">
          Última lectura del proveedor: {new Date(data.medido_en).toLocaleString("es-MX")}. El
          número sale de su API, no de los contadores del sistema operativo — ésos se
          reinician al arrancar la máquina y no coinciden con el ciclo de facturación.
        </p>
      )}
    </div>
  );
}

function Dato({
  etiqueta,
  valor,
  nota,
}: {
  etiqueta: string;
  valor: string;
  nota: string;
}) {
  return (
    <div className="rounded-lg border bg-card p-4">
      <div className="text-xs text-muted-foreground">{etiqueta}</div>
      <div className="text-xl font-semibold mt-1">{valor}</div>
      <div className="text-xs text-muted-foreground mt-1">{nota}</div>
    </div>
  );
}
